import AurigaLoader from '@components/Common/AurigaLoader';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { KPIUsageSummary } from '@services/api/deckManagement/graphql/schema/API';

interface VariantUsageSummaryProps {
  usageSummary: KPIUsageSummary | null | undefined;
  isLoading: boolean;
  expandedDeckId: string | null;
  onExpandChange: (deckId: string | null) => void;
}

/**
 * Displays variant usage across decks/pages in a collapsible accordion layout.
 * Expanded state is controlled by the parent to persist across view transitions.
 */
const VariantUsageSummary = ({ usageSummary, isLoading, expandedDeckId, onExpandChange }: VariantUsageSummaryProps) => {
  return (
    <Box className='edit-variant-usage'>
      <Typography variant='subtitle2' gutterBottom>
        Usage: {usageSummary?.total_deck_count ?? 0} decks, {usageSummary?.total_core_page_count ?? 0} pages,{' '}
        {usageSummary?.total_deckline_count ?? 0} decklines* (<em>summed across all grains</em>)
      </Typography>
      {isLoading && <AurigaLoader loaderText='Loading usage...' />}
      {usageSummary?.deck_usage?.map(
        (deck) =>
          deck && (
            <Accordion
              key={deck.deck_id}
              disableGutters
              elevation={0}
              square
              className='usage-accordion'
              expanded={expandedDeckId === deck.deck_id}
              onChange={(_, isExpanded) => onExpandChange(isExpanded ? deck.deck_id : null)}
            >
              <AccordionSummary
                expandIcon={<ChevronRightIcon className='usage-accordion-icon' />}
                className='usage-accordion-summary'
              >
                <Typography variant='body1' className='usage-deck-name'>
                  {deck.deck_name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className='usage-accordion-details'>
                {/* Can safely use the 1st CorePage sub-page to get deckline count below, as all sub-pages should be in sync */}
                <List dense>
                  {deck.page_usage?.map(
                    (corePageUsage) =>
                      corePageUsage && (
                        <ListItem key={corePageUsage.core_page_name}>
                          <ListItemText
                            primary={corePageUsage.core_page_name}
                            secondary={`${corePageUsage.pages[0]?.deckline_count ?? 0} decklines`}
                          />
                        </ListItem>
                      ),
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          ),
      )}
    </Box>
  );
};

export default VariantUsageSummary;
